import React from 'react';
import { Alert, Box, Chip, Stack, Tooltip } from '@mui/material';
import {
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import { FDataGrid, FGridMoneyCell, FGridStatusChip } from '../grid';
import {
  HistoricalFactRow,
  HistoricalImportRow,
} from './bieHistorical.models';

type Props = {
  imports: HistoricalImportRow[];
  importRowCount: number;
  importPaginationModel: GridPaginationModel;
  onImportPaginationModelChange: (model: GridPaginationModel) => void;

  facts: HistoricalFactRow[];
  factRowCount: number;
  factPaginationModel: GridPaginationModel;
  onFactPaginationModelChange: (model: GridPaginationModel) => void;

  loadingImports?: boolean;
  loadingFacts?: boolean;
  selectedImport?: HistoricalImportRow | null;
  onSelectImport: (row: HistoricalImportRow | null) => void;
};

const formatDateTime = (value: unknown): string => {
  if (!value) return '—';
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  }).format(date);
};

const CountChip = ({ value, tone }: { value: number; tone: 'success' | 'warning' | 'error' | 'default' }) => (
  <Chip size="small" label={value} color={tone === 'default' ? 'default' : tone} variant={value ? 'filled' : 'outlined'} />
);

class HistoricalImportMasterDetail extends React.PureComponent<Props> {
  importColumns: GridColDef[] = [
    { field: 'sourcecode', headerName: 'Source', width: 120 },
    { field: 'scenariocode', headerName: 'Scenario', width: 140 },
    { field: 'filename', headerName: 'File', flex: 1, minWidth: 250 },
    { field: 'recordcount', headerName: 'Records', width: 100, type: 'number' },
    { field: 'validcount', headerName: 'Valid', width: 90, align: 'center', renderCell: (p) => <CountChip value={Number(p.value || 0)} tone="success" /> },
    { field: 'warningcount', headerName: 'Warnings', width: 105, align: 'center', renderCell: (p) => <CountChip value={Number(p.value || 0)} tone="warning" /> },
    { field: 'errorcount', headerName: 'Errors', width: 90, align: 'center', renderCell: (p) => <CountChip value={Number(p.value || 0)} tone="error" /> },
    { field: 'failedcount', headerName: 'Failed', width: 90, align: 'center', renderCell: (p) => <CountChip value={Number(p.value || 0)} tone="error" /> },
    { field: 'statename', headerName: 'Status', width: 135, renderCell: FGridStatusChip },
    { field: 'importdate', headerName: 'Imported', width: 180, valueFormatter: (value: any) => formatDateTime(value) },
  ];

  factColumns: GridColDef[] = [
    { field: 'metriccode', headerName: 'Variable', width: 170 },
    { field: 'productcode', headerName: 'Product', width: 130 },
    { field: 'productname', headerName: 'Product Name', flex: 1, minWidth: 210 },
    { field: 'locationcode', headerName: 'Location', width: 125 },
    { field: 'periodcode', headerName: 'Period', width: 120 },
    { field: 'quantity', headerName: 'Quantity', width: 120, type: 'number' },
    { field: 'unitcode', headerName: 'Unit', width: 85 },
    { field: 'unitprice', headerName: 'Unit Price', width: 130, renderCell: FGridMoneyCell },
    { field: 'amount', headerName: 'Amount', width: 160, renderCell: FGridMoneyCell },
    { field: 'statename', headerName: 'Status', width: 125, renderCell: FGridStatusChip },
    {
      field: 'validationmessage', headerName: 'Validation', width: 110, sortable: false,
      renderCell: (params: GridRenderCellParams) => params.value ? (
        <Tooltip title={String(params.value)}><Chip size="small" color="warning" label="Review" /></Tooltip>
      ) : <Box component="span">—</Box>,
    },
  ];

  render() {
    return (
      <Stack spacing={2}>
        <FDataGrid
          title="Historical Imports"
          subtitle="Select an import to review the facts created from it. Counts are authoritative across all server pages."
          rows={this.props.imports}
          columns={this.importColumns}
          loading={this.props.loadingImports}
          height={390}
          masterDetail
          showSelectionControl
          selectedRowId={this.props.selectedImport?.rid ?? null}
          onSelectedRowChange={(row) => this.props.onSelectImport(row as HistoricalImportRow | null)}
          getRowId={(r: HistoricalImportRow) => r.rid}
          paginationMode="server"
          rowCount={this.props.importRowCount}
          paginationModel={this.props.importPaginationModel}
          onPaginationModelChange={this.props.onImportPaginationModelChange}
          pageSizeOptions={[10, 25, 50]}
          showQuickFilter={false}
          emptyState={{ title: 'No Historical Imports', message: 'Manual and workbook imports will appear here.' }}
        />

        {!this.props.selectedImport ? (
          <Alert severity="info">Select an import above to display its historical facts.</Alert>
        ) : (
          <FDataGrid
            title={`Facts — ${this.props.selectedImport.filename}`}
            subtitle={`${this.props.factRowCount} fact row(s) are linked to this import.`}
            rows={this.props.facts}
            columns={this.factColumns}
            loading={this.props.loadingFacts}
            height={470}
            getRowId={(r: HistoricalFactRow) => r.rid}
            paginationMode="server"
            rowCount={this.props.factRowCount}
            paginationModel={this.props.factPaginationModel}
            onPaginationModelChange={this.props.onFactPaginationModelChange}
            pageSizeOptions={[25, 50, 100]}
            showQuickFilter={false}
            emptyState={{ title: 'No Facts Found', message: 'No fact rows are linked to the selected import.' }}
          />
        )}
      </Stack>
    );
  }
}

export default HistoricalImportMasterDetail;
